/**
 * Shop-manager module helpers (docs/16): Tunisian governorate validation for
 * the Jetpack add_order API (section 6 of the official Jetpack API doc),
 * per-customer statistics computed from shiparon parcels (LTV, return rate),
 * COD risk scoring and WhatsApp deep links for the 6-operator call console.
 */
/** The 24 Tunisian governorates — Jetpack add_order rejects anything else. */
export const TUNISIAN_GOVERNORATES = [
    'Ariana',
    'Ben Arous',
    'Bizerte',
    'Béja',
    'Gabès',
    'Gafsa',
    'Jendouba',
    'Kairouan',
    'Kasserine',
    'Kébili',
    'La Manouba',
    'Le Kef',
    'Mahdia',
    'Manouba',
    'Médenine',
    'Monastir',
    'Nabeul',
    'Sfax',
    'Sidi Bouzid',
    'Siliana',
    'Sousse',
    'Tataouine',
    'Tozeur',
    'Tunis',
    'Zaghouan',
];
const normalize = (s) => s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
const BY_KEY = new Map(TUNISIAN_GOVERNORATES.map((g) => [normalize(g), g]));
/** Map free text (Woo billing state/city) to a valid Jetpack governorate, or null. */
export function matchGovernorate(input) {
    if (input === null || input === undefined)
        return null;
    const key = normalize(input);
    if (key === '')
        return null;
    const exact = BY_KEY.get(key);
    if (exact !== undefined)
        return exact;
    // fuzzy: governorate names appear inside city names (e.g. 'Tunis' in 'Le Bardo, Tunis')
    for (const [k, g] of BY_KEY) {
        if (key.includes(k) || k.includes(key))
            return g;
    }
    return null;
}
/** Normalized 8-digit Tunisian phone identity (216 prefix tolerated). */
export function phoneDigitsOf(raw) {
    if (raw === null || raw === undefined)
        return null;
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 8)
        return digits;
    if (digits.length === 11 && digits.startsWith('216'))
        return digits.slice(3);
    if (digits.length === 13 && digits.startsWith('00216'))
        return digits.slice(5);
    if (digits.length > 8)
        return digits.slice(-8);
    return digits.length >= 6 ? digits : null;
}
const DELIVERED = new Set([2, 10, 32]);
const RETURNED = new Set([5, 7, 11, 30, 31]);
/** LTV + return rate from the customer's parcel history (LTV counts delivered COD only). */
export function computeCustomerStats(parcels) {
    let deliveredCount = 0;
    let returnedCount = 0;
    let ltv = 0;
    let lifetimeValue = 0;
    let last = null;
    let lastDate = null;
    for (const p of parcels) {
        if (p.etat === 6)
            continue; // cancelled — excluded from customer history
        lifetimeValue += p.prix;
        if (DELIVERED.has(p.etat)) {
            deliveredCount += 1;
            ltv += p.prix;
        }
        else if (RETURNED.has(p.etat)) {
            returnedCount += 1;
        }
        const d = p.dateAdd === null || p.dateAdd === undefined ? null : new Date(p.dateAdd);
        if (d !== null && !Number.isNaN(d.getTime()) && (lastDate === null || d > lastDate)) {
            lastDate = d;
            last = p;
        }
    }
    const denom = deliveredCount + returnedCount;
    return {
        totalParcels: parcels.filter((p) => p.etat !== 6).length,
        deliveredCount,
        returnedCount,
        returnRate: denom === 0 ? 0 : Math.round((returnedCount / denom) * 1000) / 1000,
        ltv: Math.round(ltv * 1000) / 1000,
        lifetimeValue: Math.round(lifetimeValue * 1000) / 1000,
        lastParcelEtat: last?.etat ?? null,
        lastParcelEtatLabel: null, // label applied by caller (has getShiparonEtatLabel)
        lastParcelDate: lastDate === null ? null : lastDate.toISOString().slice(0, 10),
        lastParcelIsGhost: last?.isGhost === true,
    };
}
export function getRiskLevel(stats) {
    if (stats.totalParcels >= 2 && stats.returnedCount === 0 && stats.ltv >= 100)
        return 'vip_reliable';
    if (stats.lastParcelIsGhost === true || (stats.returnedCount > 0 && stats.lastParcelEtat === 31)) {
        return 'ghost_alert';
    }
    if (stats.returnedCount >= 2 || (stats.totalParcels >= 2 && stats.returnRate >= 0.3))
        return 'high_risk';
    if (stats.returnedCount === 1 || (stats.totalParcels >= 3 && stats.returnRate >= 0.15)) {
        return 'moderate_risk';
    }
    if (stats.totalParcels === 0)
        return 'new_customer';
    return 'standard';
}
function formatTndShort(value) {
    return new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(value);
}
/**
 * wa.me deep link for the COD call console. The phone is normalized to 8
 * Tunisian digits (+216 prefix); throws on an unusable number rather than
 * linking a wrong client.
 */
export function generateWhatsAppLink(input) {
    const digits = phoneDigitsOf(input.phone);
    if (digits === null)
        throw new Error(`invalid Tunisian phone: ${JSON.stringify(input.phone)}`);
    const name = input.customerName.trim() === '' ? 'cher client' : input.customerName.trim();
    const total = `${formatTndShort(input.totalTnd)} DT`;
    const msg = input.type === 'confirmation'
        ? `Bonjour ${name}, c'est MaPara (maparatunisie.tn). Merci pour votre commande #${input.orderNumber} (total ${total} en paiement à la livraison). Pouvez-vous confirmer votre adresse de livraison ?`
        : input.type === 'unreachable'
            ? `Bonjour ${name}, nous avons essayé de vous joindre pour votre commande #${input.orderNumber} (MaPara). Rappelez-nous ou répondez à ce message pour confirmer. Merci !`
            : `Bonjour ${name}, votre commande #${input.orderNumber} est en route (MaPara). Code de suivi : ${input.trackingCode ?? '—'}. Total à payer à la livraison : ${total}. Merci !`;
    return `https://wa.me/216${digits}?text=${encodeURIComponent(msg)}`;
}
//# sourceMappingURL=shop-manager.js.map